import React from 'react';

// Tabla compartida de VLS: envuelve el patrón de sticky-table.css
// (encabezado fijo, scroll horizontal contenido en su caja, filas con hover,
// densidad compacta/cómoda, números a la derecha y estado vacío integrado).
// Trae su propio CSS (bajo [data-vls-table]), así que NO hace falta pegar
// sticky-table.css en la app para usarla.
//
// API de composición: cada pieza es la etiqueta HTML de siempre con otro
// nombre, para migrar tablas existentes cambiando etiquetas, sin reescribir
// el map() ni las celdas.
//
// ─── Migración (mecánica) ────────────────────────────────────────────────
//   ANTES:
//     <div className="grid-scroll rounded-xl border">
//       <table className="w-full text-sm sticky-thead sticky-col1">
//         <thead><tr>
//           <th className="px-3 py-2 text-left">Nombre</th>
//           <th className="px-3 py-2 text-right">Monto</th>
//         </tr></thead>
//         <tbody>
//           {filas.length === 0 && <tr><td colSpan={2} className="p-6 text-center">Sin datos</td></tr>}
//           {filas.map((f) => (
//             <tr key={f.id} className="border-t hover:bg-muted/40">
//               <td className="px-3 py-2">{f.nombre}</td>
//               <td className="px-3 py-2 text-right tabular-nums">{f.monto}</td>
//             </tr>))}
//         </tbody>
//       </table>
//     </div>
//   DESPUÉS:
//     <Table stickyCol1 empty={filas.length === 0 && 'Sin datos'}>
//       <Table.Head><Table.Row>
//         <Table.Th>Nombre</Table.Th>
//         <Table.Th numeric>Monto</Table.Th>
//       </Table.Row></Table.Head>
//       <Table.Body>
//         {filas.map((f) => (
//           <Table.Row key={f.id}>
//             <Table.Td>{f.nombre}</Table.Td>
//             <Table.Td numeric>{f.monto}</Table.Td>
//           </Table.Row>))}
//       </Table.Body>
//     </Table>
//
//  1. Quitar de th/td el padding, borde y hover: los pone la tabla según
//     `density`. Dejar solo lo propio de la celda (ancho, font-medium, color).
//  2. Celda numérica: `numeric` (derecha + cifras tabulares), en Th y Td.
//  3. Vacío: `empty` en <Table> (string, nodo o <EmptyState/>). Si es
//     verdadero, se pinta una fila a lo ancho con el estado vacío y el
//     cuerpo se omite. Hace falta `columns` solo si la tabla no tiene Head.
//  4. Las filas clicables llevan onClick en Table.Row (cursor y foco con
//     Enter incluidos si se pasa `onClick`).
//
// Props de <Table>: density 'compacta' | 'comoda' (por defecto 'comoda'),
// stickyHeader (true), stickyCol1, stickyActions (última columna fija a la
// derecha), maxHeight (alto del scroll desde md; por defecto min(70vh, 620px);
// false = sin tope), empty, columns, caption (título accesible; visible
// solo para lectores de pantalla salvo captionVisible), framed (borde y
// radio, true), className (solo layout del contenedor), tableClassName,
// aria-label.


const CSS = `
[data-vls-table]{overflow-x:auto;-webkit-overflow-scrolling:touch;max-width:100%;background:var(--color-card,#FFFFFF)}
[data-vls-table][data-framed]{border:1px solid var(--color-border,#E7E5E4);border-radius:12px}
@media (min-width:768px){[data-vls-table][data-tope]{overflow-y:auto;max-height:var(--vls-table-max,min(70vh,620px));overscroll-behavior:contain}}
[data-vls-table] table{width:100%;border-collapse:separate;border-spacing:0;font-size:14px;color:var(--color-foreground,#1C1917)}
[data-vls-table] caption[data-oculto]{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
[data-vls-table] th{text-align:left;font-weight:600;font-size:12px;letter-spacing:.02em;color:var(--color-muted-foreground,#78716C);background:var(--color-muted,#F5F5F4);border-bottom:1px solid var(--color-border,#E7E5E4);white-space:nowrap}
[data-vls-table] td{border-bottom:1px solid var(--color-border,#E7E5E4);vertical-align:middle}
[data-vls-table] tbody tr:last-child>td{border-bottom:0}
[data-vls-table][data-densidad=comoda] th{padding:10px 12px}
[data-vls-table][data-densidad=comoda] td{padding:12px}
[data-vls-table][data-densidad=compacta] th{padding:6px 10px}
[data-vls-table][data-densidad=compacta] td{padding:6px 10px}
[data-vls-table] tbody tr[data-fila]:hover>td{background:color-mix(in srgb,var(--color-muted,#F5F5F4) 70%,var(--color-card,#FFFFFF))}
[data-vls-table] tr[data-clic]{cursor:pointer}
[data-vls-table] tr[data-clic]:focus-visible{outline:2px solid var(--color-primary,#0F7A3D);outline-offset:-2px}
[data-vls-table] [data-num]{text-align:right;font-variant-numeric:tabular-nums}
[data-vls-table] td[data-vacio]{padding:0}
[data-vls-table][data-sticky-head] thead th{position:sticky;top:0;z-index:20}
[data-vls-table][data-sticky-col1] tbody td:first-child{position:sticky;left:0;z-index:5;background:var(--color-card,#FFFFFF)}
[data-vls-table][data-sticky-col1] thead th:first-child{position:sticky;left:0;z-index:25}
[data-vls-table][data-sticky-actions] tbody td:last-child{position:sticky;right:0;z-index:5;background:var(--color-card,#FFFFFF);border-left:1px solid var(--color-border,#E7E5E4)}
[data-vls-table][data-sticky-actions] thead th:last-child{position:sticky;right:0;z-index:25;border-left:1px solid var(--color-border,#E7E5E4)}
[data-vls-table] td[data-vacio]:first-child{position:static}
`;

function contarColumnas(children) {
  let n = 0;
  React.Children.forEach(children, (hijo) => {
    if (!React.isValidElement(hijo) || n) return;
    if (hijo.type === Head) {
      React.Children.forEach(hijo.props.children, (fila) => {
        if (n || !React.isValidElement(fila)) return;
        React.Children.forEach(fila.props.children, (c) => {
          if (React.isValidElement(c)) n += Number(c.props.colSpan) || 1;
        });
      });
    }
  });
  return n;
}

function Table({
  children, density = 'comoda', stickyHeader = true, stickyCol1 = false, stickyActions = false,
  maxHeight, empty, columns, caption, captionVisible = false, framed = true,
  className = '', tableClassName = '', style, 'aria-label': ariaLabel, 'data-testid': testId,
}) {
  const vacio = !!empty && empty !== true ? empty : (empty === true ? 'No hay datos para mostrar.' : null);
  const cols = columns || contarColumnas(children) || 1;
  const hijos = vacio
    ? React.Children.map(children, (h) => (React.isValidElement(h) && h.type === Body ? null : h))
    : children;
  const contenidoVacio = vacio && (typeof vacio === 'string'
    ? <div className="text-sm" role="status" style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--color-muted-foreground, #78716C)' }}>{vacio}</div>
    : vacio);
  return (
    <>
      <div
        data-vls-table=""
        data-densidad={density}
        data-framed={framed ? '' : undefined}
        data-tope={maxHeight === false ? undefined : ''}
        data-sticky-head={stickyHeader ? '' : undefined}
        data-sticky-col1={stickyCol1 ? '' : undefined}
        data-sticky-actions={stickyActions ? '' : undefined}
        data-testid={testId}
        className={className}
        style={{ ...(maxHeight && maxHeight !== false ? { '--vls-table-max': typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight } : null), ...style }}
      >
        <style>{CSS}</style>
        <table className={tableClassName} aria-label={ariaLabel}>
          {caption && <caption data-oculto={captionVisible ? undefined : ''}>{caption}</caption>}
          {hijos}
          {vacio && (
            <tbody>
              <tr><td data-vacio="" colSpan={cols}>{contenidoVacio}</td></tr>
            </tbody>
          )}
        </table>
      </div>
    </>
  );
}

function Head({ children, ...rest }) { return <thead {...rest}>{children}</thead>; }
function Body({ children, ...rest }) { return <tbody {...rest}>{children}</tbody>; }
function Foot({ children, ...rest }) { return <tfoot {...rest}>{children}</tfoot>; }

function Row({ children, onClick, onKeyDown, ...rest }) {
  const clic = typeof onClick === 'function';
  return (
    <tr
      data-fila=""
      data-clic={clic ? '' : undefined}
      tabIndex={clic ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clic ? (e) => {
        onKeyDown?.(e);
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(e); }
      } : onKeyDown}
      {...rest}
    >
      {children}
    </tr>
  );
}

function Th({ children, numeric = false, scope = 'col', ...rest }) {
  return <th scope={scope} data-num={numeric ? '' : undefined} {...rest}>{children}</th>;
}
function Td({ children, numeric = false, ...rest }) {
  return <td data-num={numeric ? '' : undefined} {...rest}>{children}</td>;
}

Table.Head = Head;
Table.Body = Body;
Table.Foot = Foot;
Table.Row = Row;
Table.Th = Th;
Table.Td = Td;

export { Head as TableHead, Body as TableBody, Foot as TableFoot, Row as TableRow, Th as TableTh, Td as TableTd };
export default Table;
